import ProfileCustomFieldVO from '@interfaces/profiles/ProfileCustomFieldVO';
import ProfileRequestAccessVO, { GuestDetailsVO } from '@interfaces/profiles/ProfileRequestAccessVO';
import ProfileGuestMetaDataVO, { ProfileGuestMetaDataItemVO } from '@interfaces/profiles/ProfileGuestMetaDataVO';
import { extractAllMergeTagNames } from '@utils/GeneralUtils';

export const NDA_GUEST_FORM_MERGE_TAGS = ['ClientEmail', 'ClientFirstName', 'ClientLastName', 'ClientCompanyName', 'ClientJobTitle', 'ClientAddress', 'RequestDate'];

export const GUEST_FIELD_MAP: Record<string, any> = {
  clientemail: { tag: 'ClientEmail', key: 'workEmail', uiFlag: 'isEmailTag', metadataFieldTag: 'CLIENT_EMAIL' },
  clientfirstname: { tag: 'ClientFirstName', key: 'firstName', uiFlag: 'isFirstNameTag', metadataFieldTag: 'CLIENT_FIRST_NAME' },
  clientlastname: { tag: 'ClientLastName', key: 'lastName', uiFlag: 'isLastNameTag', metadataFieldTag: 'CLIENT_LAST_NAME' },
  clientaddress: { tag: 'ClientAddress', key: 'address', uiFlag: 'isAddressTag', metadataFieldTag: 'CLIENT_ADDRESS' },
  clientcompanyname: { tag: 'ClientCompanyName', key: 'companyName', uiFlag: 'isCompanyNameTag', metadataFieldTag: 'CLIENT_COMPANY_NAME' },
  clientjobtitle: { tag: 'ClientJobTitle', key: 'jobTitle', uiFlag: 'isJobTitleTag', metadataFieldTag: 'CLIENT_JOB_TITLE' },
  clientproductcategory: { tag: 'ClientProductCategory', key: 'productCategory', uiFlag: 'isProductCategoryTag', metadataFieldTag: 'CLIENT_PRODUCT_CATEGORY', accessRequestOnly: true },
  requestdate: { tag: 'RequestDate', key: 'requestDate', uiFlag: 'isDateTag' }
};

const normalizeFieldTag = (fieldTag: string): string => fieldTag.toLowerCase().replace(/_/g, '');

const WEB_FORM_FIELD_SORT_ORDER = ['clientemail', 'clientfirstname', 'clientlastname', 'clientcompanyname', 'clientjobtitle', 'clientaddress', 'requestdate', 'clientproductcategory'];

const getWebFormFieldSortIndex = (fieldTag: string): number => {
  const index = WEB_FORM_FIELD_SORT_ORDER.indexOf(normalizeFieldTag(fieldTag));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export const sortWebFormFields = (fields: ProfileCustomFieldVO[]): ProfileCustomFieldVO[] => [...fields].sort((a, b) => getWebFormFieldSortIndex(a.fieldTag) - getWebFormFieldSortIndex(b.fieldTag));

export const getWebFormFieldKey = (fieldTag: string): string | undefined => GUEST_FIELD_MAP[normalizeFieldTag(fieldTag)]?.key;

export const getEnabledWebFormFields = <T extends { fieldTag: string; isRequired?: boolean; isEnabled?: boolean }>(fields: T[] = []): T[] =>
  [...fields].filter(field => field.isEnabled || field.isRequired).sort((a, b) => getWebFormFieldSortIndex(a.fieldTag) - getWebFormFieldSortIndex(b.fieldTag));

const getGuestField = (fieldTag: string): any => GUEST_FIELD_MAP[normalizeFieldTag(fieldTag)];

const getEnabledWebFormTags = (webFormFields: ProfileCustomFieldVO[]): string[] =>
  webFormFields.flatMap(({ fieldTag, isRequired, isEnabled }) => {
    if (!isRequired && !isEnabled) return [];
    const tag = GUEST_FIELD_MAP[normalizeFieldTag(fieldTag)]?.tag;
    return tag ? [tag] : [];
  });

export const getCombinedGuestFormMergeTags = (ndaContentText: string | undefined, ndaMergeTagKeyList: string[] = [], webFormFields: ProfileCustomFieldVO[] = [], ndaType?: string): string[] => {
  const webFormTags = getEnabledWebFormTags(webFormFields);
  if (ndaType === 'DOCUSIGN') {
    return Array.from(new Set(['ClientFirstName', ...webFormTags]));
  }
  const ndaTags = extractAllMergeTagNames(ndaContentText || '', ndaMergeTagKeyList.length ? ndaMergeTagKeyList : NDA_GUEST_FORM_MERGE_TAGS);
  return Array.from(new Set([...ndaTags, ...webFormTags, GUEST_FIELD_MAP.clientcompanyname.tag, GUEST_FIELD_MAP.clientjobtitle.tag]));
};

export const getAccessRequestMergeTags = (webFormFields: ProfileCustomFieldVO[] = []): string[] => getEnabledWebFormTags(webFormFields);

export const getVisibleGuestFormMergeTags = (mergeTagNames: string[] = [], hasSharedViews = true): string[] =>
  hasSharedViews ? mergeTagNames : mergeTagNames.filter(tag => tag !== GUEST_FIELD_MAP.clientproductcategory.tag);

export const getResolvedGuestDetails = (webFormDetails?: Partial<GuestDetailsVO> | null, requestAccess?: Partial<ProfileRequestAccessVO> | null, options?: Record<string, any>): GuestDetailsVO => {
  const params = requestAccess?.additionalParams as Record<string, string> | undefined;
  return {
    firstName: options?.ndaType === 'DOCUSIGN' ? options?.docusignDetails?.docusignRecipientSignedMap?.name || '' : requestAccess?.firstName || '',
    lastName: requestAccess?.lastName || webFormDetails?.lastName || '',
    address: requestAccess?.address || webFormDetails?.address || '',
    companyName: params?.companyName || requestAccess?.companyName || webFormDetails?.companyName || '',
    jobTitle: params?.jobTitle || requestAccess?.jobTitle || webFormDetails?.jobTitle || '',
    productCategory: Array.isArray(requestAccess?.productCategory)
      ? requestAccess.productCategory.filter(Boolean)
      : Array.isArray(webFormDetails?.productCategory)
        ? webFormDetails.productCategory.filter(Boolean)
        : []
  };
};

const hasRequiredGuestDetails = (mergeTagNames: string[], details: GuestDetailsVO, options?: { mailId?: string }): boolean =>
  mergeTagNames.every(tag => {
    const field = getGuestField(tag);
    if (!field?.key) return true;
    // Email comes from the verified session, not GuestDetailsVO.
    if (field.key === 'workEmail') {
      return Boolean((options?.mailId || '').trim());
    }
    const value = details[field.key as keyof GuestDetailsVO];
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(typeof value === 'string' ? value.trim() : value);
  });

export const shouldCollectGuestDetails = (
  mergeTagNames: string[],
  webFormDetails?: Partial<GuestDetailsVO> | null,
  requestAccess?: Partial<ProfileRequestAccessVO> | null,
  options?: Record<string, any>
): boolean => {
  const visibleMergeTags = getVisibleGuestFormMergeTags(mergeTagNames, options?.hasSharedViews !== false);
  return (
    visibleMergeTags.length > 0 &&
    !hasRequiredGuestDetails(visibleMergeTags, getResolvedGuestDetails(webFormDetails, requestAccess, options), {
      mailId: requestAccess?.mailId || options?.mailId
    })
  );
};

const emptyGuestDetails = (): GuestDetailsVO => ({ firstName: '', lastName: '', address: '', companyName: '', jobTitle: '', productCategory: [] });

const GUEST_NAME_PATTERN = /^[\p{L}]+(?:[ '\-][\p{L}]+)*$/u;

export const isValidVisitorRequestGuestName = (value?: string): boolean => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return true;
  }
  return GUEST_NAME_PATTERN.test(trimmed);
};

export const splitVisitorRequestFullName = (fullName?: string): { firstName: string; lastName: string } => {
  const trimmedFullName = String(fullName || '').trim();
  if (!trimmedFullName) {
    return { firstName: '', lastName: '' };
  }
  const spaceIndex = trimmedFullName.indexOf(' ');
  if (spaceIndex === -1) {
    return { firstName: trimmedFullName, lastName: '' };
  }
  return {
    firstName: trimmedFullName.slice(0, spaceIndex).trim(),
    lastName: trimmedFullName.slice(spaceIndex + 1).trim()
  };
};

export const toVerifyAccessMetadata = (details?: Partial<GuestDetailsVO>) => {
  const metadata: Record<string, string> = {};
  Object.values(GUEST_FIELD_MAP).forEach(field => {
    if (!field?.key || field.accessRequestOnly || !field.metadataFieldTag) return;
    const value = details?.[field.key as keyof GuestDetailsVO];
    if (value) metadata[field.metadataFieldTag] = value;
  });
  return metadata;
};

export const toGuestDetailsVO = (guestMetaData?: ProfileGuestMetaDataVO | null) => {
  if (!guestMetaData?.metadata?.length) return null;
  const details = emptyGuestDetails();
  let hasValue = false;
  for (const { fieldTag, value } of guestMetaData.metadata) {
    const key = getGuestField(String(fieldTag || ''))?.key as keyof GuestDetailsVO | undefined;
    if (!key || value == null || value === '') continue;

    if (key === 'productCategory') {
      if (!Array.isArray(value)) continue;
      const categories = value.map(item => String(item).trim()).filter(Boolean);
      if (!categories.length) continue;
      details.productCategory = categories;
    } else if (typeof value === 'string') {
      details[key] = value;
    } else {
      continue;
    }
    hasValue = true;
  }
  return hasValue ? details : null;
};

export const toVisitorRequestSubmitMetadata = (details?: { firstName?: string; lastName?: string; fullName?: string; jobTitle?: string; companyName?: string }): ProfileGuestMetaDataItemVO[] => {
  const trimmedFullName = String(details?.fullName || '').trim();
  let firstName = String(details?.firstName || '').trim();
  let lastName = String(details?.lastName || '').trim();
  if (!firstName && !lastName && trimmedFullName) {
    const spaceIndex = trimmedFullName.indexOf(' ');
    if (spaceIndex === -1) {
      firstName = trimmedFullName;
    } else {
      firstName = trimmedFullName.slice(0, spaceIndex).trim();
      lastName = trimmedFullName.slice(spaceIndex + 1).trim();
    }
  }
  const metadata: ProfileGuestMetaDataItemVO[] = [];
  const append = (fieldKey: string, value: string) => {
    const fieldTag = GUEST_FIELD_MAP[fieldKey]?.metadataFieldTag;
    if (fieldTag && value) {
      metadata.push({ fieldTag, value });
    }
  };
  append('clientfirstname', firstName);
  append('clientlastname', lastName);
  append('clientjobtitle', String(details?.jobTitle || '').trim());
  append('clientcompanyname', String(details?.companyName || '').trim());
  return metadata;
};
